// ---------------------------------------------------------------------------
// Source panel view preferences — per-project, browser-local.
//
// The Design tab opens with a "Design source" card (8 count tiles, the file
// name + revision line, the local-revision chip and the source-roundtrip
// sha256) stacked ABOVE the canvas. Measured 2026-07-30 on the audited
// WonBanking source at 1600x1000: that card is 299 px tall and pushes the
// design board's first pixel to y=577, so a creator who came to look at a
// design reads a statistics table first. Collapsing it is the fix, and the
// choice has to survive reopening the project or it is not a fix at all.
//
// Why its own key rather than a field on the workspace: the workspace object
// carries the whole readback, and for this exact source
// (`[공통] 영업점검색_UI.fig`, 2.7 MB / 4,939 nodes) `localStorage` already
// rejects it with QuotaExceededError — see the module header of
// `openpencil-source-workspace-storage.ts`. A view preference stored inside
// that payload would inherit the same failure. This record is one boolean.
//
// Why a status instead of a boolean return: the same quota/private-mode
// failure applies here in miniature. A silent `catch {}` (the prevailing
// pattern in `ide-v2-slice.ts`) would let the interface imply the choice was
// remembered when nothing was written. Callers get `"rejected"` and say so.
// ---------------------------------------------------------------------------

/** Namespaced beside `cogo.openpencil.sourceWorkspace.<projectId>`. */
const COLLAPSE_KEY_PREFIX = "cogo.openpencil.sourceSummaryCollapsed.";

/** Same fallback identity the workspace storage uses when no project is open. */
export const LOCAL_SOURCE_PREFERENCE_SCOPE = "workspace_local";

export type SourcePreferenceWriteState = "stored" | "rejected";

export function sourceSummaryCollapsedKey(projectId?: string | null): string {
  return `${COLLAPSE_KEY_PREFIX}${projectId || LOCAL_SOURCE_PREFERENCE_SCOPE}`;
}

/**
 * `null` means "this project has never recorded a choice" — deliberately
 * distinct from `false`, so the caller can apply a first-visit default without
 * mistaking it for an explicit "keep it open".
 */
export function readSourceSummaryCollapsed(
  projectId?: string | null,
): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(sourceSummaryCollapsedKey(projectId));
    if (raw === "true") return true;
    if (raw === "false") return false;
    return null;
  } catch {
    // Private mode / disabled storage: unknown, not "expanded".
    return null;
  }
}

export function writeSourceSummaryCollapsed(
  collapsed: boolean,
  projectId?: string | null,
): SourcePreferenceWriteState {
  if (typeof window === "undefined") return "rejected";
  try {
    window.localStorage.setItem(
      sourceSummaryCollapsedKey(projectId),
      collapsed ? "true" : "false",
    );
    return "stored";
  } catch (error) {
    console.warn("[openpencil-source] view preference not stored", error);
    return "rejected";
  }
}
